import { motion } from "framer-motion";
import { ArrowDown, Clapperboard } from "lucide-react";

const lines = [
  <>Construire</>,
  <><em className="italic text-terra">votre</em> maison,</>,
  <>pas à pas.</>,
];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  return (
    <section className="pt-32 lg:pt-40 pb-16 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="hero-section">
      <p
        className="font-sub text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-terra mb-8"
        data-testid="hero-overline"
      >
        Le guide pour construire sa maison
      </p>
      <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[1.05] tracking-tight mb-8" data-testid="hero-title">
        {lines.map((line, i) => (
          <span key={i} className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 0.2 + i * 0.15, ease: EASE }}
            >
              {line}
            </motion.span>
          </span>
        ))}
      </h1>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 mb-16">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
          className="text-base sm:text-lg leading-relaxed text-ink/70 max-w-xl"
          data-testid="hero-subtitle"
        >
          Du choix du terrain à la remise des clés : vidéos, simulateurs et guides
          pour maîtriser votre budget, votre recherche et votre projet — sans jargon.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
          className="flex items-center gap-4"
        >
          <a
            href="#simulateurs"
            data-testid="hero-cta-budget"
            className="inline-flex items-center gap-2 bg-ink text-bone font-sub text-sm font-medium px-6 py-3.5 rounded-full hover:bg-terra hover:-translate-y-0.5 transition-[background-color,transform] duration-200"
          >
            Estimer mon budget
          </a>
          <a
            href="#etapes"
            data-testid="hero-cta-steps"
            className="inline-flex items-center gap-2 font-sub text-sm font-medium px-6 py-3.5 rounded-full border border-ink/20 hover:border-ink hover:-translate-y-0.5 transition-[border-color,transform] duration-200"
          >
            Les étapes
            <ArrowDown className="w-4 h-4" />
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, delay: 1.05, ease: EASE }}
        data-testid="hero-video"
      >
        <div className="flex items-center gap-3 mb-5">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-terra/10 text-terra">
            <Clapperboard className="w-4 h-4" />
          </span>
          <div>
            <p className="font-sub text-xs uppercase tracking-[0.2em] text-stone2">Guide vidéo</p>
            <p className="font-sub font-bold text-sm sm:text-base">Quel budget pouvez-vous emprunter ? Capacité, apport et mensualité</p>
          </div>
        </div>
        <div className="rounded-xl overflow-hidden shadow-deep border border-border bg-ink">
          <div className="aspect-video">
            <iframe
              src="https://www.youtube-nocookie.com/embed/52YWWZkEptE?rel=0"
              title="Construire sa maison : le guide complet pas à pas"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              data-testid="hero-video-iframe"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}
